/**
 * Environment Indicator Component
 *
 * Displays the current environment (Local, Development, Production)
 * and API target to help developers understand their configuration.
 *
 * Shows combined auth source + API target (e.g., "Dev Auth + Local API")
 */

import React from 'react';
import type { EnvironmentName, ApiTarget } from '../config/types';
import { useTheme, spacing } from '@fti/design-system';

interface EnvIndicatorProps {
  /** Environment name (auth source) */
  environmentName: EnvironmentName;
  /** Human-readable environment display name */
  displayName: string;
  /** Whether using mock authentication */
  isMock: boolean;
  /** API target (may differ from auth environment in dev mode) */
  apiTarget?: ApiTarget;
}

/**
 * Generate display text based on auth environment and API target
 */
function getDisplayText(
  displayName: string,
  environmentName: EnvironmentName,
  isMock: boolean,
  apiTarget?: ApiTarget
): string {
  // Mock mode: always show "(Mock)"
  if (isMock) {
    return `${displayName} (Mock)`;
  }

  // Production: always just "Production" (no API target switching)
  if (environmentName === 'prod') {
    return 'Production';
  }

  // Dev mode with API target specified
  if (apiTarget && apiTarget !== environmentName) {
    const apiLabel = apiTarget.charAt(0).toUpperCase() + apiTarget.slice(1);
    return `${displayName} Auth + ${apiLabel} API`;
  }

  // Default: just the display name
  return displayName;
}

/**
 * Environment indicator badge
 *
 * Shows current environment with color coding using FTI Design System semantic colors:
 * - Local (Mock): Info (blue-teal)
 * - Development: Warning (yellow/amber)
 * - Production: Error (red)
 */
export function EnvIndicator({
  environmentName,
  displayName,
  isMock,
  apiTarget
}: EnvIndicatorProps): JSX.Element {
  const { theme } = useTheme();

  // Get semantic colors based on environment
  const getEnvironmentColors = () => {
    switch (environmentName) {
      case 'local':
        return {
          background: theme.colors.semantic.info.light,
          border: theme.colors.semantic.info.default,
        };
      case 'dev':
        return {
          background: theme.colors.semantic.warning.light,
          border: theme.colors.semantic.warning.default,
        };
      case 'prod':
        return {
          background: theme.colors.semantic.error.light,
          border: theme.colors.semantic.error.default,
        };
      default:
        return {
          background: theme.colors.neutral[100],
          border: theme.colors.neutral[300],
        };
    }
  };

  const envColors = getEnvironmentColors();
  const text = getDisplayText(displayName, environmentName, isMock, apiTarget);

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: `${spacing.xs} ${spacing.sm}`,
        borderRadius: '0.375rem',
        background: envColors.background,
        border: `1px solid ${envColors.border}`,
        fontSize: '0.8rem',
        fontWeight: 500,
        color: theme.colors.neutral[900],
      }}
      title={`Auth: ${displayName}${apiTarget ? ` | API: ${apiTarget}` : ''}${isMock ? ' (Mock)' : ''}`}
    >
      {text}
    </div>
  );
}
